import { RefObject, useEffect, useRef, useState } from 'react';
import { useRect } from '@dx-ui/utilities-use-rect';
import cx from 'classnames';
import {
  useWindowScroll,
  useLayoutStableCallback,
  useUserInteractedWithPage,
} from '../header.utilities';
import { useDeviceDetect } from '@dx-ui/utilities-use-device-detect';
import { useRouter } from 'next/router';
import { useFixedHeaderHeight } from './fixed-header-context';

const FixedHeader = ({
  showNavOnScrollUp = true,
  shopComponent,
  navComponent,
  isTranslucent,
  isReimagineThemeExperience,
  isShopFormInHeroMedia,
  headerRef,
}: {
  showNavOnScrollUp?: boolean;
  shopComponent: React.ReactNode;
  navComponent: React.ReactNode;
  brandCode?: string;
  isTranslucent?: boolean;
  isReimagineThemeExperience?: boolean;
  isShopFormInHeroMedia?: boolean;
  headerRef?: RefObject<HTMLDivElement | null>;
}) => {
  const isServer = typeof window === 'undefined';
  const headerContentRef = useRef<HTMLDivElement | null>(null);
  const headerContentRect = useRect({ ref: headerContentRef });
  const headerContentHeight = headerContentRect?.height || 0;
  const shopFormRef = useRef<HTMLDivElement | null>(null);
  const shopFormRect = useRect({ ref: shopFormRef });
  const shopFormHeight = shopFormRect?.height || 0;
  const router = useRouter();
  const hash = router.asPath?.split('#')?.[1];
  const { setHeaderHeight, setVisibleHeaderHeight, shouldHeaderOverlapTop, setIsNavHidden } =
    useFixedHeaderHeight();

  const isMobile = useDeviceDetect()?.isMobile;
  const scrollDistance = useWindowScroll(isMobile ? 30 : 45).scrollY || 0;
  const scrollDirection = useWindowScroll(isMobile ? 30 : 45).scrollDirection || 'down';
  const [isScrolledPastHeaderContent, setIsScrolledPastHeaderContent] = useState(false);
  const [showHeaderContent, setShowHeaderContent] = useState(true);
  const [disableStickyHeader, setDisableStickyHeader] = useState(false);

  const [hasHydrated, setHasHydrated] = useState(false);
  const shouldUseFixedHeader = shouldHeaderOverlapTop
    ? !disableStickyHeader
    : !isServer && hasHydrated && !disableStickyHeader;
  const hideNav = isScrolledPastHeaderContent && !showHeaderContent && !isMobile;

  const isUserInteracted = useUserInteractedWithPage(!hash);

  useLayoutStableCallback(() => {
    if (!!hash && !isUserInteracted) {
      const element = document.getElementById(hash);
      element?.scrollIntoView();
    }
  }, !hash);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    const isTotalHeightAvailable = isShopFormInHeroMedia
      ? headerContentHeight > 0
      : headerContentHeight > 0 && shopFormHeight > 0;
    const totalHeight = headerContentHeight + shopFormHeight;
    setHeaderHeight(isTotalHeightAvailable ? totalHeight : 0);
  }, [headerContentHeight, shopFormHeight, setHeaderHeight, disableStickyHeader]);

  useEffect(() => {
    const visibleHeight = hideNav ? shopFormHeight : headerContentHeight + shopFormHeight;
    setVisibleHeaderHeight(disableStickyHeader ? 0 : visibleHeight);
  }, [hideNav, headerContentHeight, shopFormHeight, setVisibleHeaderHeight, disableStickyHeader]);

  useEffect(() => {
    setIsNavHidden(hideNav);
  }, [hideNav, setIsNavHidden]);

  useEffect(() => {
    const isTooShortForStickyHeader = shopFormHeight > (window?.innerHeight || 0) * 0.33;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isTooShortForStickyHeader || prefersReducedMotion || isMobile) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisableStickyHeader(true);
    } else {
      // Add scroll-padding to html element to prevent focused content from being hidden behind the sticky header
      document.documentElement.style.scrollPaddingTop = `${headerContentHeight + shopFormHeight}px`;
    }
    if (!isMobile) {
      if (scrollDistance > headerContentHeight) {
        setIsScrolledPastHeaderContent(true);
        if (scrollDirection === 'up') {
          if (showNavOnScrollUp) {
            document.documentElement.style.scrollPaddingTop = `${
              headerContentHeight + shopFormHeight
            }px`;
            setShowHeaderContent(true);
          }
        } else if (scrollDirection === 'down') {
          document.documentElement.style.scrollPaddingTop = `${shopFormHeight}px`;
          setShowHeaderContent(false);
        }
      } else {
        setIsScrolledPastHeaderContent(false);
      }
    }
  }, [
    headerContentHeight,
    scrollDirection,
    scrollDistance,
    shopFormHeight,
    showNavOnScrollUp,
    hideNav,
    isMobile,
  ]);
  return (
    <>
      <div
        ref={headerRef}
        className={cx(
          'z-10 flex w-full flex-col duration-200 ease-out [&:has(:focus-within):not(:has(.osc-flyover-shop-form:focus-within))]:!translate-y-0 [&:has([aria-expanded="true"]:not(.osc-flyover-shop-form_*))]:!translate-y-0',
          {
            'bg-transparent': isTranslucent,
            fixed: shouldUseFixedHeader,
            relative: !shouldUseFixedHeader,
          }
        )}
        style={{
          transform:
            hideNav && shouldUseFixedHeader ? `translateY(-${headerContentHeight || 0}px)` : 'none',
        }}
      >
        <div className="absolute start-0 top-0 z-0 size-full" />
        <div className="relative z-10">
          <div
            className={cx('relative z-20', {
              'bg-black-linear-gradient': isTranslucent && isReimagineThemeExperience,
              'bg-blur': isTranslucent && !isReimagineThemeExperience,
            })}
            ref={headerContentRef}
          >
            {navComponent}
          </div>
          <div
            ref={shopFormRef}
            className="osc-flyover-shop-form relative z-10 flex w-full items-center"
          >
            {isShopFormInHeroMedia ? undefined : <div className="w-full">{shopComponent}</div>}
          </div>
        </div>
      </div>
      {!shouldHeaderOverlapTop && shouldUseFixedHeader ? (
        <div
          className="block w-full"
          style={{
            height: `${headerContentHeight + shopFormHeight}px`,
          }}
        />
      ) : null}
      {shouldHeaderOverlapTop && disableStickyHeader ? (
        <div
          className="block w-full"
          style={{
            marginBottom: `-${headerContentHeight + shopFormHeight}px`,
          }}
        />
      ) : null}
    </>
  );
};

export { FixedHeader };
